import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Stack,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Paper,
} from "@mui/material";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import RepeatIcon from "@mui/icons-material/Repeat";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import EventIcon from "@mui/icons-material/Event";
import { 
  FriendlyTextField, 
  FriendlyButton, 
  PRESET_AMOUNTS, 
  SPLIT_PERIOD_PRESETS,
  SPLIT_FREQUENCY_OPTIONS,
  RECURRING_PRESETS,
  calculateSurcharge, 
  calculateTotal,
  calculateSplitSchedule,
  formatPaymentDate,
} from "../shared";

/**
 * PromiseForm Component
 * Handles donation promise creation (amount, frequency, etc.)
 * Note: A "promise" is the commitment to donate - can be created without immediate payment
 */
const PromiseForm = ({
  t,
  organization,
  campaigns = [],
  defaultCampaignId,
  initialData = {},
  onSubmit,
  isLoading = false,
  error = null,
  paymentMethod = "card",
  acceptRecurring = false,
}) => {
  const [amount, setAmount] = useState(initialData.amount || "");
  const [paymentType, setPaymentType] = useState(
    initialData.paymentType || "one_time"
  );
  // Split payment states
  const [splitPeriod, setSplitPeriod] = useState(
    initialData.splitPeriod || "3_months"
  );
  const [splitFrequency, setSplitFrequency] = useState(
    initialData.splitFrequency || "Monthly"
  );
  const [customPayments, setCustomPayments] = useState(
    initialData.customPayments || 6
  );
  const [startDate, setStartDate] = useState(initialData.startDate || "");

  // Recurring payment states
  const [recurringFrequency, setRecurringFrequency] = useState(
    initialData.recurringFrequency || "Monthly"
  );
  const [recurringStartDate, setRecurringStartDate] = useState(
    initialData.recurringStartDate || ""
  );
  const [recurringEndDate, setRecurringEndDate] = useState(
    initialData.recurringEndDate || ""
  );

  const isSurchargeActive = organization?.is_surcharge_active || false;
  const isSurchargeMandatory = organization?.is_surcharge_mandatory || false;

  const [surchargeEnabled, setSurchargeEnabled] = useState(
    initialData.surchargeEnabled || isSurchargeMandatory || false
  );
  const [notes, setNotes] = useState(initialData.notes || "");
  const [selectedCampaignId, setSelectedCampaignId] = useState(
    defaultCampaignId || campaigns[0]?._id || ""
  );
  const [validationError, setValidationError] = useState("");

  const selectedCampaign = useMemo(
    () => campaigns.find((item) => item?._id === selectedCampaignId) || campaigns[0],
    [campaigns, selectedCampaignId]
  );

  const presetAmounts = useMemo(() => {
    const campaignPresets = Array.isArray(selectedCampaign?.kiosk_preset)
      ? selectedCampaign.kiosk_preset
          .map(Number)
          .filter((value) => Number.isFinite(value) && value > 0)
      : [];
    return campaignPresets.length ? campaignPresets : PRESET_AMOUNTS;
  }, [selectedCampaign]);

  const parseDateInputAsLocal = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value !== "string") return null;

    const [year, month, day] = value.split("-").map(Number);
    if (
      Number.isFinite(year) &&
      Number.isFinite(month) &&
      Number.isFinite(day)
    ) {
      const parsed = new Date(year, month - 1, day);
      if (Number.isNaN(parsed.getTime())) return null;

      if (
        parsed.getFullYear() !== year ||
        parsed.getMonth() !== month - 1 ||
        parsed.getDate() !== day
      ) {
        return null;
      }

      return parsed;
    }

    const fallback = new Date(value);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  };

  const toStartOfDay = (date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

  // Calculate surcharge FIRST (needed for split schedule)
  const surchargeValue = useMemo(() => {
    // Surcharge is only calculated if it's active
    if (!isSurchargeActive) return 0;
    // Pass the organization object directly - calculateSurcharge handles extracting surcharge_rate
    return calculateSurcharge(
      surchargeEnabled,
      amount,
      paymentMethod,
      organization
    );
  }, [
    surchargeEnabled,
    amount,
    paymentMethod,
    organization,
    isSurchargeActive,
  ]);

  const surchargePercentage = useMemo(() => {
    const raw = organization?.surcharge_rate || 0;
    const percent = raw > 1 ? raw : raw * 100;
    return Math.round(percent * 100) / 100;
  }, [organization]);

  // Calculate total amount including surcharge
  const totalAmount = useMemo(() => {
    return calculateTotal(amount, surchargeEnabled, surchargeValue);
  }, [amount, surchargeEnabled, surchargeValue]);

  // Calculate split payment schedule - uses totalAmount (includes surcharge if enabled)
  const splitSchedule = useMemo(() => {
    if (paymentType !== "split" || !amount || !startDate) {
      return null;
    }

    const preset = SPLIT_PERIOD_PRESETS.find(p => p.value === splitPeriod);
    let numberOfPayments;
    let frequency;

    if (splitPeriod === "custom") {
      // For custom, use the selected frequency and custom number of payments
      frequency = splitFrequency;
      numberOfPayments = Math.max(2, customPayments);
    } else {
      // Use preset values - months equals number of monthly payments
      numberOfPayments = preset?.months || 3;
      frequency = preset?.frequency || "Monthly";
    }

    // Use totalAmount (which includes surcharge if enabled) for split calculation
    return calculateSplitSchedule(
      totalAmount,
      numberOfPayments,
      frequency,
      startDate
    );
  }, [totalAmount, paymentType, splitPeriod, splitFrequency, customPayments, startDate]);

  const handleAmountPreset = (preset) => {
    setAmount(preset.toString());
  };

  const handleCustomAmount = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, "");
    setAmount(value);
  };

  const handleSubmit = (promiseOnly = false) => {
    setValidationError("");

    // Validate split payment start date
    if (paymentType === "split" && startDate) {
      const startDateTime = parseDateInputAsLocal(startDate);
      if (!startDateTime) {
        setValidationError(
          t("invalid_split_start_date", "Please select a valid first payment date.")
        );
        return;
      }
      const minAllowedDate = new Date(2026, 2, 23); // March 23, 2026
      
      // Set both dates to start of day for fair comparison
      const startDateStartOfDay = toStartOfDay(startDateTime);
      
      if (startDateStartOfDay < minAllowedDate) {
        setValidationError(
          t(
            "split_start_date_too_early",
            "First payment date must be on or after March 23, 2026."
          )
        );
        return;
      }
    }

    // Validate recurring payment start date
    if (paymentType === "recurring" && recurringStartDate) {
      const startDateTime = parseDateInputAsLocal(recurringStartDate);
      if (!startDateTime) {
        setValidationError(
          t("invalid_recurring_start_date", "Please select a valid recurring start date.")
        );
        return;
      }
      const minAllowedDate = new Date(2026, 2, 23); // March 23, 2026
      
      const startDateStartOfDay = toStartOfDay(startDateTime);
      
      if (startDateStartOfDay < minAllowedDate) {
        setValidationError(
          t(
            "recurring_start_date_too_early",
            "Recurring start date must be on or after March 23, 2026."
          )
        );
        return;
      }
    }

    // Build frequency value from split settings
    let frequency = null;
    let endDate = null;
    
    if (paymentType === "split" && splitSchedule) {
      const preset = SPLIT_PERIOD_PRESETS.find(p => p.value === splitPeriod);
      frequency = splitPeriod === "custom" ? splitFrequency : (preset?.frequency || "Monthly");
      endDate = splitSchedule.endDate?.toISOString();
    }

    // Build frequency and dates for recurring payment
    if (paymentType === "recurring") {
      frequency = recurringFrequency;
      endDate = recurringEndDate || null;
    }

    onSubmit({
      campaignId: selectedCampaignId,
      amount,
      paymentType,
      frequency,
      startDate: paymentType === "split" ? startDate : (paymentType === "recurring" ? recurringStartDate : null),
      endDate,
      numberOfPayments: splitSchedule?.numberOfPayments || null,
      perPaymentAmount: splitSchedule?.perPaymentAmount || null,
      surchargeEnabled,
      notes,
      promiseOnly,
    });
  };

  const isValid = Number(amount) > 0 && 
    (paymentType === "one_time" || 
     (paymentType === "split" && startDate && splitSchedule?.payments?.length >= 2) ||
     (paymentType === "recurring" && recurringStartDate && recurringFrequency));

  return (
    <Box sx={{ maxWidth: 600, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            backgroundColor: "primary.100",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2,
          }}
        >
          <VolunteerActivismIcon sx={{ fontSize: 40, color: "primary.main" }} />
        </Box>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: "primary.main", mb: 1 }}
        >
          {t("make_promise", "Make a Promise")}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t("promise_description", "Choose your donation amount and frequency")}
        </Typography>
      </Box>

      <Stack spacing={4}>
        {/* Campaign Selection (if multiple) */}
        {campaigns.length > 1 && (
          <FormControl fullWidth>
            <InputLabel>{t("select_campaign", "Select Campaign")}</InputLabel>
            <Select
              value={selectedCampaignId}
              onChange={(e) => setSelectedCampaignId(e.target.value)}
              label={t("select_campaign", "Select Campaign")}
            >
              {campaigns.map((c) => (
                <MenuItem key={c._id} value={c._id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Payment Type Selection */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            {t("donation_type", "Donation Type")}
          </Typography>
          <ToggleButtonGroup
            value={paymentType}
            exclusive
            onChange={(_, val) => val && setPaymentType(val)}
            fullWidth
            sx={{
              flexWrap: { xs: "wrap", sm: "nowrap" },
              gap: { xs: 1, sm: 0 },
              "& .MuiToggleButtonGroup-grouped": {
                border: "1px solid",
                borderColor: "divider",
                borderRadius: { xs: 1, sm: 0 },
                "&:not(:first-of-type)": {
                  ml: { xs: 0, sm: "-1px" },
                  borderLeft: { xs: "1px solid", sm: 0 },
                  borderLeftColor: { xs: "divider", sm: "transparent" },
                },
                "&:not(:last-of-type)": {
                  borderRight: "1px solid",
                  borderRightColor: "divider",
                },
              },
              "& .MuiToggleButton-root": {
                py: 2,
                minWidth: 0,
                flex: { xs: "1 1 100%", sm: "1 1 0" },
                whiteSpace: "normal",
                lineHeight: 1.25,
                borderRadius: 2,
                fontWeight: 600,
                "&.Mui-selected": {
                  backgroundColor: "primary.main",
                  color: "white",
                  "&:hover": { backgroundColor: "primary.dark" },
                },
              },
            }}
          >
            <ToggleButton value="one_time">
              <VolunteerActivismIcon sx={{ mr: 1 }} />
              {t("one_time", "One-Time")}
            </ToggleButton>
            <ToggleButton value="split">
              <CallSplitIcon sx={{ mr: 1 }} />
              {t("split_payment", "Split Payment")}
            </ToggleButton>
            {acceptRecurring && (
              <ToggleButton value="recurring">
                <RepeatIcon sx={{ mr: 1 }} />
                {t("recurring_payment", "Recurring")}
              </ToggleButton>
            )}
          </ToggleButtonGroup>
        </Box>

        {/* Amount Selection */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            {t("select_amount", "Select Amount")}
          </Typography>
          <Grid container spacing={1.5}>
            {presetAmounts.map((preset) => (
              <Grid item xs={4} sm={3} key={preset}>
                <FriendlyButton
                  variant={amount === preset.toString() ? "contained" : "outlined"}
                  onClick={() => handleAmountPreset(preset)}
                  fullWidth
                  sx={{ py: 1.5 }}
                >
                  ${preset}
                </FriendlyButton>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 2 }}>
            <FriendlyTextField
              label={t("custom_amount", "Custom Amount")}
              value={amount}
              onChange={handleCustomAmount}
              placeholder="0.00"
              InputProps={{
                startAdornment: (
                  <Typography sx={{ mr: 1, color: "text.secondary" }}>$</Typography>
                ),
              }}
            />
          </Box>
        </Box>

        {/* Split Payment Options */}
        {paymentType === "split" && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              {t("split_period", "Split Over")}
            </Typography>
            <Grid container spacing={1.5}>
              {SPLIT_PERIOD_PRESETS.map((opt) => (
                <Grid item xs={6} sm={4} key={opt.value}>
                  <FriendlyButton
                    variant={splitPeriod === opt.value ? "contained" : "outlined"}
                    onClick={() => setSplitPeriod(opt.value)}
                    fullWidth
                    sx={{ py: 1.5 }}
                  >
                    {t(opt.labelKey, opt.defaultLabel)}
                  </FriendlyButton>
                </Grid>
              ))}
            </Grid>

            {/* Custom Period Options */}
            {splitPeriod === "custom" && (
              <Box sx={{ mt: 3, p: 2, backgroundColor: "grey.50", borderRadius: 2 }}>
                <Stack spacing={2}>
                  <TextField
                    label={t("number_of_payments", "Number of Payments")}
                    type="number"
                    value={customPayments}
                    onChange={(e) => setCustomPayments(Math.max(2, parseInt(e.target.value) || 2))}
                    fullWidth
                    inputProps={{ min: 2, max: 60 }}
                  />
                  <FormControl fullWidth>
                    <InputLabel>{t("payment_frequency", "Payment Frequency")}</InputLabel>
                    <Select
                      value={splitFrequency}
                      onChange={(e) => setSplitFrequency(e.target.value)}
                      label={t("payment_frequency", "Payment Frequency")}
                    >
                      {SPLIT_FREQUENCY_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {t(opt.labelKey, opt.defaultLabel)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Box>
            )}

            <Box sx={{ mt: 3 }}>
              <TextField
                label={t("start_date", "First Payment Date")}
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: "2026-03-23" }}
              />
            </Box>

            {/* Split Payment Preview */}
            {splitSchedule && splitSchedule.payments.length > 0 && (
              <Paper 
                elevation={0}
                sx={{ 
                  mt: 3, 
                  p: 2, 
                  backgroundColor: "info.50", 
                  border: "1px solid",
                  borderColor: "info.200",
                  borderRadius: 2 
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <EventIcon color="info" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "info.main" }}>
                    {t("payment_schedule", "Payment Schedule")}
                  </Typography>
                </Stack>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t(
                    "split_payment_summary",
                    `Your total of $${totalAmount.toFixed(2)} will be split into ${splitSchedule.numberOfPayments} payments`
                  )}
                </Typography>

                {/* Show surcharge info if applicable */}
                {surchargeEnabled && surchargeValue > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    {t("includes_surcharge", `(Includes ${surchargePercentage}% processing fee: $${surchargeValue.toFixed(2)})`)}
                  </Typography>
                )}

                <Divider sx={{ my: 1.5 }} />

                <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
                  {splitSchedule.payments.map((payment, index) => (
                    <Stack 
                      key={index}
                      direction="row" 
                      justifyContent="space-between" 
                      alignItems="center"
                      sx={{ 
                        py: 1,
                        borderBottom: index < splitSchedule.payments.length - 1 ? "1px dashed" : "none",
                        borderColor: "grey.300"
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            backgroundColor: index === 0 ? "primary.main" : "grey.300",
                            color: index === 0 ? "white" : "text.secondary",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                          }}
                        >
                          {payment.paymentNumber}
                        </Box>
                        <Typography variant="body2">
                          {formatPaymentDate(payment.date)}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ${payment.amount.toFixed(2)}
                      </Typography>
                    </Stack>
                  ))}
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("final_payment_date", "Final Payment")}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "primary.main" }}>
                    {formatPaymentDate(splitSchedule.endDate)}
                  </Typography>
                </Stack>
              </Paper>
            )}
          </Box>
        )}

        {/* Recurring Payment Options */}
        {paymentType === "recurring" && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              {t("how_often_donate", "How Often Would You Like to Donate?")}
            </Typography>
            
            {/* Recurring Frequency Presets - Card Style for clarity */}
            <Stack spacing={1.5}>
              {RECURRING_PRESETS.map((preset) => (
                <Paper
                  key={preset.value}
                  elevation={0}
                  onClick={() => setRecurringFrequency(preset.value)}
                  sx={{
                    p: 2,
                    cursor: "pointer",
                    border: "2px solid",
                    borderColor: recurringFrequency === preset.value ? "primary.main" : "grey.300",
                    borderRadius: 2,
                    backgroundColor: recurringFrequency === preset.value ? "primary.50" : "white",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: recurringFrequency === preset.value ? "primary.main" : "grey.400",
                      backgroundColor: recurringFrequency === preset.value ? "primary.50" : "grey.50",
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: 600, 
                          color: recurringFrequency === preset.value ? "primary.main" : "text.primary",
                          fontSize: "1.1rem"
                        }}
                      >
                        {t(preset.labelKey, preset.defaultLabel)}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {t(preset.description, preset.defaultDescription)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        border: "2px solid",
                        borderColor: recurringFrequency === preset.value ? "primary.main" : "grey.400",
                        backgroundColor: recurringFrequency === preset.value ? "primary.main" : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {recurringFrequency === preset.value && (
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            backgroundColor: "white",
                          }}
                        />
                      )}
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>

            {/* Start Date */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                {t("when_to_start", "When Should We Start?")}
              </Typography>
              <TextField
                label={t("first_payment_date", "First Payment Date")}
                type="date"
                value={recurringStartDate}
                onChange={(e) => setRecurringStartDate(e.target.value)}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: "2026-03-23" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: "1.1rem",
                    "& input": { py: 2 }
                  }
                }}
              />
            </Box>

            {/* End Date (Optional) */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                {t("when_to_stop", "When Should We Stop? (Optional)")}
              </Typography>
              <TextField
                label={t("last_payment_date", "Last Payment Date")}
                type="date"
                value={recurringEndDate}
                onChange={(e) => setRecurringEndDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ 
                  min: recurringStartDate || "2026-03-23" 
                }}
                helperText={t("recurring_end_date_helper", "Leave empty to continue until you cancel")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: "1.1rem",
                    "& input": { py: 2 }
                  }
                }}
              />
            </Box>

            {/* Recurring Payment Summary - Clear visual feedback */}
            {recurringStartDate && recurringFrequency && Number(amount) > 0 && (
              <Paper 
                elevation={0}
                sx={{ 
                  mt: 3, 
                  p: 2.5, 
                  backgroundColor: "success.50", 
                  border: "2px solid",
                  borderColor: "success.200",
                  borderRadius: 2 
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <RepeatIcon sx={{ color: "success.main" }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "success.main" }}>
                    {t("your_recurring_plan", "Your Recurring Donation Plan")}
                  </Typography>
                </Stack>
                
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body1" color="text.secondary">
                      {t("amount_each_time", "Amount Each Time")}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      ${Number(amount).toFixed(2)}
                    </Typography>
                  </Stack>
                  
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body1" color="text.secondary">
                      {t("frequency_label", "Frequency")}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {RECURRING_PRESETS.find(p => p.value === recurringFrequency)?.defaultLabel || recurringFrequency}
                    </Typography>
                  </Stack>
                  
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body1" color="text.secondary">
                      {t("starting_on", "Starting On")}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatPaymentDate(new Date(recurringStartDate))}
                    </Typography>
                  </Stack>
                  
                  {recurringEndDate && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body1" color="text.secondary">
                        {t("ending_on", "Ending On")}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {formatPaymentDate(new Date(recurringEndDate))}
                      </Typography>
                    </Stack>
                  )}
                  
                  {!recurringEndDate && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mt: 1 }}>
                      {t("ongoing_until_cancel", "This donation will continue until you choose to cancel it.")}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            )}
          </Box>
        )}

        {/* Notes (optional) */}
        <FriendlyTextField
          label={t("notes_optional", "Notes (Optional)")}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          rows={2}
          placeholder={t("notes_placeholder", "Any special instructions or dedication...")}
        />

        {/* Surcharge Option - Only show toggle if active but not mandatory */}
        {isSurchargeActive && !isSurchargeMandatory && (
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: surchargeEnabled ? "success.50" : "grey.50",
              border: "2px solid",
              borderColor: surchargeEnabled ? "success.main" : "grey.200",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onClick={() => setSurchargeEnabled(!surchargeEnabled)}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {t("cover_fees", "Cover Processing Fees")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t(
                    "cover_fees_desc_rate",
                    `Add ${surchargePercentage}% to cover transaction costs`
                  )}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  border: "2px solid",
                  borderColor: surchargeEnabled ? "success.main" : "grey.400",
                  backgroundColor: surchargeEnabled
                    ? "success.main"
                    : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {surchargeEnabled && (
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: "white",
                    }}
                  />
                )}
              </Box>
            </Stack>
          </Box>
        )}

        {/* Mandatory Surcharge Notice */}
        {isSurchargeActive && isSurchargeMandatory && (
          <Alert severity="info" sx={{ py: 1.5 }}>
            {t(
              "mandatory_surcharge_notice",
              `A ${surchargePercentage}% processing fee is automatically included to cover transaction costs.`
            )}
          </Alert>
        )}

        {/* Summary */}
        {Number(amount) > 0 && (
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: "primary.50",
              border: "1px solid",
              borderColor: "primary.200",
            }}
          >
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">
                  {t("donation_amount", "Donation Amount")}
                </Typography>
                <Typography sx={{ fontWeight: 500 }}>
                  ${Number(amount).toFixed(2)}
                </Typography>
              </Stack>
              {surchargeEnabled && surchargeValue > 0 && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    {t("processing_fee", "Processing Fee")}
                  </Typography>
                  <Typography sx={{ fontWeight: 500 }}>
                    ${surchargeValue.toFixed(2)}
                  </Typography>
                </Stack>
              )}
              <Box sx={{ borderTop: "1px solid", borderColor: "primary.200", pt: 1, mt: 1 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontWeight: 700, color: "primary.main" }}>
                    {t("total", "Total")}
                  </Typography>
                  <Typography sx={{ fontWeight: 700, color: "primary.main", fontSize: "1.25rem" }}>
                    ${totalAmount.toFixed(2)}
                    {paymentType === "split" && splitSchedule && (
                      <Typography component="span" variant="body2" color="text.secondary">
                        {" "}({splitSchedule.numberOfPayments} × ${splitSchedule.perPaymentAmount.toFixed(2)})
                      </Typography>
                    )}
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </Box>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ py: 1.5 }}>
            {error}
          </Alert>
        )}

        {validationError && (
          <Alert severity="warning" sx={{ py: 1.5 }}>
            {validationError}
          </Alert>
        )}

        {/* Promise Note */}
        <Alert severity="info" sx={{ py: 1.5 }}>
          {t(
            "promise_note",
            "A promise is your commitment to donate. You can pay now or save it and pay later."
          )}
        </Alert>

        {/* Action Buttons */}
        <Stack spacing={2}>
          <FriendlyButton
            onClick={() => handleSubmit(false)}
            disabled={!isValid || isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
          >
            {t("continue_to_payment", "Continue to Payment")}
          </FriendlyButton>
          
          <FriendlyButton
            variant="outlined"
            onClick={() => handleSubmit(true)}
            disabled={!isValid || isLoading}
            startIcon={<BookmarkBorderIcon />}
          >
            {t("save_promise_only", "Save Promise (Pay Later)")}
          </FriendlyButton>
        </Stack>
      </Stack>
    </Box>
  );
};

export default PromiseForm;
